import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Scroll from 'react-scroll';
import Panel from 'components/general/panel';

import { ArrayHelpers, DeleteWarning, Forms, Modal, SaveButton, SystemText } from '@salesperformanceint/e4shared';

import { saveAppraisal } from 'actions/profile';
import { setSaving, setShowSuccess } from 'actions/ui';
import { addDays, e4Format } from 'helpers/dates';

import '../../appraisals.css';
import { apiDelete, apiGet, apiPatch } from 'services';
import { coachRoles, isCoach } from 'helpers/profile';

const today = new Date();
const defaultSchedule = addDays(new Date(), 14);
const defaultLaunch = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 9, 0);

export const calculateAppraisalStatus = (appraisal, isUser, isManager) => {
    if (!appraisal) return '';
    if (appraisal.submission)
        return SystemText('Reviews/Appraisals/AppraisalSummary/Statuses/Completed', e4Format(appraisal.submission));
    if (appraisal.started) return SystemText('Reviews/Appraisals/AppraisalSummary/Statuses/InProgress');
    if (appraisal.user_Comments_Done && appraisal.manager_Comments_Done)
        return SystemText('Reviews/Appraisals/AppraisalSummary/Statuses/ReadyToStart');
    if (isUser && appraisal.participants.indexOf('U') >= 0 && !appraisal.user_Comments_Done)
        return SystemText('Reviews/Appraisals/AppraisalSummary/Statuses/AwaitingComments/Self');
    if (isManager && appraisal.participants.indexOf('M') >= 0 && !appraisal.manager_Comments_Done)
        return SystemText('Reviews/Appraisals/AppraisalSummary/Statuses/AwaitingComments/Self');
    if (isUser && appraisal.participants.indexOf('M') >= 0 && !appraisal.manager_Comments_Done)
        return SystemText('Reviews/Appraisals/AppraisalSummary/Statuses/AwaitingComments/Manager');
    if (appraisal.participants.indexOf('U') >= 0 && !appraisal.user_Comments_Done)
        return `${SystemText('Reviews/Appraisals/AppraisalSummary/Statuses/AwaitingComments/Other', appraisal.full_Name)}`;
    if (appraisal.participants.indexOf('M') >= 0 && !appraisal.manager_Comments_Done)
        return `${SystemText('Reviews/Appraisals/AppraisalSummary/Statuses/AwaitingComments/Other')} ${
            appraisal.manager_Full_Name
        }`;
    return SystemText('Reviews/Appraisals/AppraisalSummary/Statuses/Unknown');
};

export const AdminViewSummary = ({ appraisal, userType, user, loadAppraisals }) => {
    const dispatch = useDispatch();

    const [showUndoComment, setShowUndoComment] = useState(false);

    const {
        user_Comments_Done: userCommentsDoneDate,
        manager_Comments_Done: managerCommentsDoneDate,
        full_Name,
        manager_Full_Name,
    } = appraisal;

    const isManager = userType === 'manager';
    const name = isManager ? manager_Full_Name : full_Name;
    let date = null;

    if (isManager && Boolean(managerCommentsDoneDate)) {
        date = e4Format(managerCommentsDoneDate);
    } else if (!isManager && Boolean(userCommentsDoneDate)) {
        date = e4Format(userCommentsDoneDate);
    }

    const booleanForLookup = isManager ? Boolean(managerCommentsDoneDate) : Boolean(userCommentsDoneDate);

    const messageLookup = React.useMemo(
        () => ({
            true: SystemText('Reviews/Appraisals/AppraisalSummary/AdminViewSummary/Status/Complete', name, date),
            false: SystemText('Reviews/Appraisals/AppraisalSummary/AdminViewSummary/Status/Waiting', name),
        }),
        [appraisal, userType]
    );

    const undoCommentConfirm = (confirm, userType) => {
        if (confirm) {
            apiPatch(`appraisal-reset-comment/${userType}`, appraisal, user.guid, () => {
                dispatch(
                    setShowSuccess(SystemText('Reviews/Appraisals/AppraisalSummary/AdminViewSummary/ShowSuccess', name))
                );
                loadAppraisals();
            });
        }
        setShowUndoComment(false);
    };

    return (
        <div data-testid="admin-view-summary-testid" className="mb5 appraisals-admin-view-summary">
            {user.administrator && booleanForLookup && (
                <button
                    className="btn btn-xs btn-danger appraisals-summary-undo-button mr10"
                    data-testid="undo-button-testid"
                    title={SystemText('Reviews/Appraisals/AppraisalSummary/AdminViewSummary/Buttons/Revert/Title')}
                    onClick={() => setShowUndoComment(true)}
                >
                    <i className="fa fa-undo mr5" />
                </button>
            )}
            <div data-testid={`status-text-${booleanForLookup}-testid`}>{messageLookup[booleanForLookup]}</div>
            <DeleteWarning
                message={SystemText('Reviews/Appraisals/AppraisalSummary/AdminViewSummary/DeleteWarning')}
                show={showUndoComment}
                close={confirm => undoCommentConfirm(confirm, userType)}
            />
        </div>
    );
};

export const EditAppraisal = ({ appraisal, profile, user, coaching, reload, close, show, loadAppraisals, permissions }) => {
    if (!appraisal) {
        return null;
    }

    const dispatch = useDispatch();

    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [launch, setLaunch] = useState(defaultLaunch);
    const [scheduled, setScheduled] = useState(defaultSchedule);
    const [complete, setComplete] = useState(true);
    const [participants, setParticipants] = useState('UM');
    const [competencyIds, setCompetencyIds] = useState([]);
    const [competencies, setCompetencies] = useState([]);
    const [errors, setErrors] = useState({});
    const [launchDisabled, setLaunchDisabled] = useState(false);

    const { saving } = useSelector(state => state.ui.saving);

    const isAdmin = user?.administrator;
    const isUsersCoach = isCoach(profile?.id, user.companySettings, coaching, coachRoles.manageReviews);
    const isUser = user?.id === appraisal?.profile_Id;
    const isManager = user?.id === appraisal?.manager_Id;

    const isAdminOrCoach = isAdmin || isUsersCoach;

    useEffect(() => {
        if (appraisal && !appraisal?.submission) {
            const tempLaunch = appraisal?.launch ?? defaultLaunch;
            setScheduled(appraisal?.scheduled ?? defaultSchedule);
            setLaunch(tempLaunch);
            setLaunchDisabled(new Date(tempLaunch) < new Date());
            setComplete(appraisal?.complete ?? true);
            setCompetencyIds(appraisal?.competency_Ids ?? []);
            setParticipants(appraisal?.participants ?? 'UM');
        }
    }, [appraisal]);

    useEffect(() => {
        profile?.id &&
            apiGet(`competencies-by-user?ids=${profile?.id}`, user.guid, response =>
                setCompetencies(
                    response.map(r => ({ id: r.id, name: r.full_Name })).sort((a, b) => (a.name > b.name ? 1 : -1))
                )
            );
    }, [profile?.id]);

    const validate = appraisal => {
        let result = true;
        const errors = {};

        if (!appraisal?.scheduled) {
            errors.title = SystemText('Reviews/Appraisals/Edit/Errors/Schedule');
            result = false;
        }

        if (appraisal?.scheduled < appraisal?.launch) {
            errors.launch = SystemText('Reviews/Appraisals/Edit/Errors/Launch');
            result = false;
        }

        if (!complete && !ArrayHelpers.existsAndHasLength(competencyIds)) {
            errors.competencies = SystemText('ReviewTracker/Schedule/Fields/Competencies/Error');
            result = false;
        }

        setErrors(errors);
        if (!result) document.getElementsByClassName('modal')[0].scrollTop = 0;

        return result;
    };

    const closeModal = begin => {
        reload();
        close(begin);
    };

    const save = () => {
        const newAppraisal = appraisal?.id
            ? {
                  ...appraisal,
                  launch,
                  scheduled,
              }
            : {
                  scheduled,
                  complete,
                  launch,
                  competency_Ids: competencyIds,
                  profile_Id: profile?.id,
                  manager_Id: profile?.manager_Id,
                  coach_Id: isUsersCoach ? user.id : null,
              };

        if (validate(newAppraisal)) {
            dispatch(
                saveAppraisal(newAppraisal, res => {
                    dispatch(setShowSuccess(SystemText('Reviews/Appraisals/Edit/ShowSuccess')));
                    closeModal(true);
                    loadAppraisals();
                    setLaunch(defaultLaunch);
                    setScheduled(defaultSchedule);
                    setComplete(true);
                    setCompetencyIds([]);
                    setParticipants('UM');
                })
            );
        } else {
            dispatch(setSaving(false));
        }
    };
    if (!profile) return null;

    const onSave = saving => {
        dispatch(setSaving(saving));
    };

    const deleteAppraisal = confirm => {
        setShowDeleteWarning(false);
        if (confirm) {
            apiDelete(`appraisal/${appraisal.profile_Id}`, null, user.guid, () => {
                dispatch(setShowSuccess(SystemText('Reviews/Appraisals/Edit/Delete/Success')));
                reload?.(true);
                close?.();
            });
        }
    };

    const allowDelete = appraisal && permissions.reviews.canDelete && isAdmin;

    const allowSave =
        permissions.reviews.canEditSchedule && (isAdmin || !appraisal?.owner_Id || appraisal?.owner_Id === user.id);

    const footer = (
        <div>
            {allowDelete && (
                <button
                    className="pull-left btn btn-danger"
                    onClick={() => setShowDeleteWarning(true)}
                    data-testid="button-appraisal-delete"
                >
                    {SystemText('Reviews/Appraisals/Edit/Buttons/Delete')}
                </button>
            )}
            <div className="pull-right">
                <SaveButton
                    disabled={!allowSave}
                    data-testid="schedule-appraisal-save-button-testid"
                    saving={saving}
                    setSaving={onSave}
                    className="btn btn-success"
                    onClick={() => save()}
                >
                    {SystemText('Reviews/Appraisals/Edit/Buttons/Save')}
                </SaveButton>
                <button
                    data-testid="schedule-appraisal-cancel-button-testid"
                    className="btn"
                    onClick={() => closeModal(false)}
                >
                    {SystemText('Reviews/Appraisals/Edit/Buttons/Cancel')}
                </button>
            </div>
        </div>
    );

    const title = SystemText('Reviews/Appraisals/Edit/Title/Edit');
    const help = SystemText('Reviews/Appraisals/Edit/Help/Edit');
    const panelMessage = [
        SystemText('Reviews/Appraisals/Edit/Panel/1', profile?.first_Name),
        SystemText('Reviews/Appraisals/Edit/Panel/2', profile?.first_Name),
    ];

    const status = React.useMemo(
        () => calculateAppraisalStatus(appraisal, isUser, isManager),
        [appraisal, isUser, isManager]
    );

    const renderAppraisalStatus = React.useCallback(
        (isAdminOrCoach, started) => {
            return (
                <div className="mb10">
                    <label className="appraisals-label" data-testid="btn-appraisal-status">
                        {SystemText('Reviews/Appraisals/AppraisalSummary/Labels/Status/Label')}
                    </label>
                    {isAdminOrCoach && !started ? (
                        <div data-testid="admin-view-testid">
                            {appraisal?.participants.indexOf('U') >= 0 && (
                                <AdminViewSummary
                                    appraisal={appraisal}
                                    userType="user"
                                    user={user}
                                    loadAppraisals={loadAppraisals}
                                />
                            )}
                            {appraisal?.participants.indexOf('M') >= 0 && (
                                <AdminViewSummary
                                    appraisal={appraisal}
                                    userType="manager"
                                    user={user}
                                    loadAppraisals={loadAppraisals}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="appraisals-summary-status">{status}</div>
                    )}
                </div>
            );
        },
        [isAdmin, status, appraisal]
    );

    return (
        <Modal show={show} title={title} close={() => closeModal(false)} footer={footer}>
            <Scroll.Element name="top-of-modal" />
            <div
                data-testid="schedule-form-wrapper-testid"
                className="form-group"
                id="manage-schedule-form"
                style={{ marginBottom: '20px' }}
            >
                <Forms.Label label={SystemText('Reviews/Appraisals/Edit/Form/Label')} value={profile?.full_Name} />
                <Forms.Label
                    label={SystemText('Reviews/Appraisals/AppraisalSummary/Labels/Manager/Label')}
                    value={appraisal.manager_Full_Name}
                />
                <Forms.Label
                    label={SystemText('Reviews/Appraisals/AppraisalSummary/Labels/Created/Label')}
                    value={e4Format(appraisal.created)}
                />
                {renderAppraisalStatus(isAdminOrCoach, appraisal?.started)}
                <Forms.Date
                    data-testid="field-launch-date"
                    showTime
                    mandatory
                    value={launch}
                    help={SystemText('Reviews/Appraisals/Edit/Fields/Launch/Help')}
                    onChange={value => setLaunch(value)}
                    label={SystemText('Reviews/Appraisals/Edit/Fields/Launch/Label')}
                    error={errors?.launch}
                    disabled={launchDisabled}
                />
                <Forms.Date2
                    error={errors?.schedule}
                    last
                    label={SystemText('Reviews/Appraisals/Edit/Fields/Date/Label')}
                    mandatory
                    onChange={value => setScheduled(value)}
                    help={help}
                    value={scheduled}
                />
                <Forms.Toggle
                    data-testid="field-full-toggle"
                    value={complete}
                    disabled
                    readonly
                    help={SystemText('ReviewTracker/Schedule/Fields/Full/Help')}
                    onChange={value => setComplete(value)}
                    label={SystemText('ReviewTracker/Schedule/Fields/Full/Label')}
                />
                {!complete && (
                    <Forms.Select
                        data-testid="field-comps-select"
                        disabled
                        readonly
                        value={competencyIds}
                        data={competencies || []}
                        error={errors?.competencies}
                        allowMultiple
                        help={SystemText('ReviewTracker/Schedule/Fields/Competencies/Help')}
                        onChange={value => setCompetencyIds(value)}
                        label={SystemText('ReviewTracker/Schedule/Fields/Competencies/Label')}
                    />
                )}
                {user.reviewTypes?.length > 1 && (
                    <>
                        <Forms.Buttons
                            data-testid="field-participants-toggle"
                            help={SystemText('ReviewTracker/Schedule/Fields/Participants/Help')}
                            label={SystemText('ReviewTracker/Schedule/Fields/Participants/Label')}
                            yesLabel={SystemText('ReviewTracker/Schedule/Fields/Participants/Options/Both')}
                        >
                            {user.reviewTypes?.some(x => x === 'U') && (
                                <button disabled className={`btn ${participants === 'U' ? 'btn-primary' : ''}`}>
                                    {SystemText('ReviewTracker/Schedule/Fields/Participants/Options/User')}
                                </button>
                            )}
                            {user.reviewTypes?.some(x => x === 'M') && (
                                <button disabled className={`btn ${participants === 'M' ? 'btn-primary' : ''}`}>
                                    {SystemText('ReviewTracker/Schedule/Fields/Participants/Options/Manager')}
                                </button>
                            )}
                            {user.reviewTypes?.some(x => x === 'UM') && (
                                <button disabled className={`btn ${participants === 'UM' ? 'btn-primary' : ''}`}>
                                    {SystemText('ReviewTracker/Schedule/Fields/Participants/Options/Both')}
                                </button>
                            )}
                        </Forms.Buttons>
                        {isUsersCoach && (
                            <div className="mtm15 mb20 text-sm">
                                {SystemText('ReviewTracker/Schedule/Fields/Participants/CoachExplainer')}
                            </div>
                        )}
                    </>
                )}
                <div data-testid="schedule-warning-panel-testid" className="scheduler-panel">
                    <Panel colour="warning" icon="exclamation-triangle" message={panelMessage} />
                </div>
            </div>
            <DeleteWarning
                message={SystemText('Reviews/Appraisals/Edit/Delete/Warning')}
                show={showDeleteWarning}
                close={confirm => deleteAppraisal(confirm)}
            />
        </Modal>
    );
};

export default EditAppraisal;
